import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { updateGiveaway, purgeGiveaway } from "../services/giveawayService";
import SettingsSection from "../AdminSetting/SettingsSection";
import HelpButton from "../AdminSetting/HelpButton";
import { apiProblem, etInputToIso, isoToEtInput, showProblem, toastOk, fmtEt } from "./giveawayUtils";
import { ActionModal } from "./GiveawayUi";

// Every setting of one giveaway. Dates are typed in Toronto time. Only the
// parts that changed are sent; sponsor, prize and eligibility always go as a
// whole object because the server replaces them whole.

const LOCKED = ["drawn", "completed", "archived"];

const lines = (text) =>
  String(text || "")
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);

const toForm = (g) => ({
  name: g.name || "",
  nameFr: g.nameFr || "",
  city: g.city || "",
  pocketSlug: g.pocketSlug || "",
  opensAt: isoToEtInput(g.opensAt),
  closesAt: isoToEtInput(g.closesAt),
  sponsor: {
    legalName: g.sponsor?.legalName || "",
    mailingAddress: g.sponsor?.mailingAddress || "",
    contactEmail: g.sponsor?.contactEmail || "",
  },
  prize: {
    description: g.prize?.description || "",
    value: g.prize?.value ?? "",
    currency: g.prize?.currency || "CAD",
    provider: g.prize?.provider || "",
    providerIsSponsor: g.prize?.providerIsSponsor === true,
    restrictions: g.prize?.restrictions || "",
    descriptionFr: g.prize?.descriptionFr || "",
    restrictionsFr: g.prize?.restrictionsFr || "",
  },
  eligibility: {
    minAge: g.eligibility?.minAge ?? "",
    areaText: g.eligibility?.areaText || "",
    areaTextFr: g.eligibility?.areaTextFr || "",
    cities: (g.eligibility?.cities || []).join("\n"),
    excludedCities: (g.eligibility?.excludedCities || []).join("\n"),
    excludedMessage: g.eligibility?.excludedMessage || "",
    quebecIncluded: g.eligibility?.quebecIncluded === true,
    quebecFilingDone: g.eligibility?.quebecFilingDone === true,
  },
  rulesVersion: g.rulesVersion || "",
  rulesApproved: g.rulesApproved === true,
  frenchRulesApproved: g.frenchRulesApproved === true,
  prizePurchased: g.prizePurchased === true,
  drawDateText: g.drawDateText || "",
  winnerResponseDays: g.winnerResponseDays ?? "",
  retentionDays: g.retentionDays ?? "",
  emailsEnabled: g.emailsEnabled === true,
  appEntry: {
    enabled: g.appEntry?.enabled === true,
    newMembers: g.appEntry?.newMembers !== false,
    existingMembers: g.appEntry?.existingMembers !== false,
    pushOnEntry: g.appEntry?.pushOnEntry === true,
  },
  announcement: {
    enabled: g.announcement?.enabled === true,
    at: isoToEtInput(g.announcement?.at),
    push: g.announcement?.push !== false,
    reminderAt: isoToEtInput(g.announcement?.reminderAt),
  },
});

const num = (v) => (v === "" || v == null ? null : Number(v));

// The form, shaped the way the server wants it.
const toPayload = (f) => ({
  name: f.name.trim(),
  nameFr: f.nameFr.trim(),
  city: f.city.trim(),
  pocketSlug: f.pocketSlug || null,
  opensAt: etInputToIso(f.opensAt),
  closesAt: etInputToIso(f.closesAt),
  sponsor: {
    legalName: f.sponsor.legalName.trim(),
    mailingAddress: f.sponsor.mailingAddress.trim(),
    contactEmail: f.sponsor.contactEmail.trim(),
  },
  prize: {
    description: f.prize.description.trim(),
    value: num(f.prize.value),
    currency: f.prize.currency.trim(),
    provider: f.prize.provider.trim(),
    providerIsSponsor: f.prize.providerIsSponsor,
    restrictions: f.prize.restrictions.trim(),
    descriptionFr: f.prize.descriptionFr.trim(),
    restrictionsFr: f.prize.restrictionsFr.trim(),
  },
  eligibility: {
    minAge: num(f.eligibility.minAge),
    areaText: f.eligibility.areaText.trim(),
    areaTextFr: f.eligibility.areaTextFr.trim(),
    cities: lines(f.eligibility.cities),
    excludedCities: lines(f.eligibility.excludedCities),
    excludedMessage: f.eligibility.excludedMessage.trim(),
    quebecIncluded: f.eligibility.quebecIncluded,
    quebecFilingDone: f.eligibility.quebecFilingDone,
  },
  rulesVersion: f.rulesVersion.trim(),
  rulesApproved: f.rulesApproved,
  frenchRulesApproved: f.frenchRulesApproved,
  prizePurchased: f.prizePurchased,
  drawDateText: f.drawDateText.trim(),
  winnerResponseDays: num(f.winnerResponseDays),
  retentionDays: num(f.retentionDays),
  emailsEnabled: f.emailsEnabled,
  appEntry: { ...f.appEntry },
  announcement: { enabled: f.announcement.enabled, at: etInputToIso(f.announcement.at), push: f.announcement.push, reminderAt: etInputToIso(f.announcement.reminderAt) },
});

const SECTIONS = [
  { id: "basics", icon: "mdi:calendar-clock-outline", title: "Name and dates" },
  { id: "sponsor", icon: "mdi:domain", title: "Sponsor" },
  { id: "prize", icon: "mdi:gift-outline", title: "Prize" },
  { id: "eligibility", icon: "mdi:map-marker-radius-outline", title: "Who can enter" },
  { id: "rules", icon: "mdi:scale-balance", title: "Rules, draw and data" },
  { id: "emails", icon: "mdi:email-outline", title: "Emails" },
  { id: "app", icon: "mdi:cellphone-check", title: "Entry through the app" },
];

const SECTION_KEYS = {
  basics: ["name", "nameFr", "city", "opensAt", "closesAt"],
  sponsor: ["sponsor"],
  prize: ["prize"],
  eligibility: ["eligibility", "pocketSlug"],
  rules: ["rulesVersion", "rulesApproved", "frenchRulesApproved", "prizePurchased", "drawDateText", "winnerResponseDays", "retentionDays"],
  emails: ["emailsEnabled"],
  app: ["appEntry", "announcement"],
};

const Field = ({ label, help, name, helpOpen, toggleHelp, children, hint }) => (
  <div className="mb-16">
    <label className="form-label text-sm fw-semibold d-flex align-items-center gap-2">
      {label}
      {help ? <HelpButton name={`gw-${name}`} help={help} open={helpOpen === name} onToggle={() => toggleHelp(name)} /> : null}
    </label>
    {children}
    {helpOpen === name && help ? (
      <div id={`help-panel-gw-${name}`} className="text-xs text-secondary-light mt-1 p-8 radius-4 bg-neutral-50">
        {help}
      </div>
    ) : null}
    {hint ? <div className="text-xs text-secondary-light mt-1">{hint}</div> : null}
  </div>
);

const Check = ({ label, checked, onChange, disabled, switchStyle }) => (
  <div className={`form-check ${switchStyle ? "form-switch" : ""} mb-12 d-flex align-items-center gap-2`}>
    <input
      className="form-check-input mt-0"
      type="checkbox"
      role={switchStyle ? "switch" : undefined}
      style={switchStyle ? { width: "3em", height: "1.5em", cursor: "pointer" } : undefined}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
    <label className="form-check-label text-sm">{label}</label>
  </div>
);

// "Copy area from": fills the area from another giveaway; nothing is saved until Save.
const CopyArea = ({ sources, onCopy }) => {
  const [pick, setPick] = useState(sources[0]?._id || "");
  const src = sources.find((x) => x._id === pick);
  return (
    <div className="p-12 radius-8 mb-16" style={{ background: "rgba(140, 80, 253, 0.10)" }}>
      <div className="text-sm fw-semibold mb-8">Copy the area from another giveaway</div>
      <div className="d-flex gap-2 flex-wrap">
        <select className="form-select" style={{ flex: 1, minWidth: 220 }} value={pick} onChange={(e) => setPick(e.target.value)}>
          {sources.map((x) => (
            <option key={x._id} value={x._id}>
              {x.name} ({(x.eligibility?.cities || []).length} communities)
            </option>
          ))}
        </select>
        <button type="button" className="btn btn-outline-primary btn-sm px-16" disabled={!src} onClick={() => src && onCopy(src)}>
          Copy area
        </button>
      </div>
      <div className="text-xs text-secondary-light mt-8">
        Fills the city list, the area wording, the Quebec setting and the pocket. You can still edit them after.
      </div>
    </div>
  );
};

// The eligible cities as removable chips with a search box. The form keeps them
// as one city per line, the shape the rest of the form already uses.
const CityList = ({ value, onChange, disabled }) => {
  const [q, setQ] = useState("");
  const [all, setAll] = useState(false);
  const list = lines(value);
  const key = (c) => c.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const term = key(q.trim());
  const shown = term ? list.filter((c) => key(c).includes(term)) : all ? list : list.slice(0, 24);
  const exists = term && list.some((c) => key(c) === term);
  const add = () => {
    const c = q.trim();
    if (!c || exists) return;
    onChange([...list, c].join("\n"));
    setQ("");
  };
  const remove = (c) => onChange(list.filter((x) => x !== c).join("\n"));
  return (
    <div>
      <div className="d-flex gap-2 mb-8">
        <input
          className="form-control"
          placeholder="Search or add a city"
          value={q}
          disabled={disabled}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        {term && !exists && !disabled ? (
          <button type="button" className="btn btn-outline-primary btn-sm px-12 text-nowrap" onClick={add}>
            Add "{q.trim()}"
          </button>
        ) : null}
      </div>
      <div className="d-flex flex-wrap gap-1" style={{ maxHeight: 220, overflowY: "auto" }}>
        {shown.map((c) => (
          <span key={c} className="badge bg-neutral-200 text-neutral-900 d-inline-flex align-items-center gap-1 px-8 py-4 radius-4 fw-normal text-sm">
            {c}
            {!disabled ? (
              <button type="button" className="btn btn-link p-0 lh-1 text-secondary-light" aria-label={`Remove ${c}`} onClick={() => remove(c)}>
                ×
              </button>
            ) : null}
          </span>
        ))}
        {term && !shown.length ? <span className="text-xs text-secondary-light">Not on the list.</span> : null}
      </div>
      {!term && list.length > 24 ? (
        <button type="button" className="btn btn-link p-0 mt-8 text-sm" onClick={() => setAll((a) => !a)}>
          {all ? "Show fewer" : `Show all ${list.length}`}
        </button>
      ) : null}
    </div>
  );
};

const GiveawaySettingsTab = ({ g, reload, onUnauthorized }) => {
  const original = useMemo(() => toForm(g), [g]);
  const [form, setForm] = useState(original);
  const [open, setOpen] = useState(["basics"]);
  const [helpOpen, setHelpOpen] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeEarly, setPurgeEarly] = useState(false);

  useEffect(() => {
    setForm(original);
    setReason("");
  }, [original]);

  const locked = LOCKED.includes(g.status);
  const hasEntries = (g.counts?.total || 0) > 0;

  const base = useMemo(() => toPayload(original), [original]);
  const next = toPayload(form);
  const changedKeys = Object.keys(next).filter((k) => JSON.stringify(next[k]) !== JSON.stringify(base[k]));
  const dirtyIn = (sec) => SECTION_KEYS[sec].filter((k) => changedKeys.includes(k)).length;

  const set = (path, value) =>
    setForm((f) => {
      const [a, b] = path.split(".");
      if (!b) return { ...f, [a]: value };
      return { ...f, [a]: { ...f[a], [b]: value } };
    });

  const toggle = (id) => setOpen((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));
  const toggleHelp = (name) => setHelpOpen((h) => (h === name ? null : name));

  const datesBad = form.opensAt && form.closesAt && etInputToIso(form.opensAt) >= etInputToIso(form.closesAt);
  const reasonShort = hasEntries && reason.trim().length > 0 && reason.trim().length < 10;

  const save = async () => {
    if (!changedKeys.length || locked) return;
    const body = {};
    changedKeys.forEach((k) => {
      body[k] = next[k];
    });
    if (hasEntries && reason.trim()) body.reason = reason.trim();
    setSaving(true);
    try {
      await updateGiveaway(g._id, body);
      toastOk("Settings saved");
      await reload();
    } catch (e) {
      if (e?.response?.status === 401) return onUnauthorized();
      showProblem(apiProblem(e, "Could not save the settings."), "Not saved");
    } finally {
      setSaving(false);
    }
  };

  const summary = {
    basics: `${form.name || "No name"} · ${form.city || "No city"} · closes ${
      form.closesAt ? fmtEt(etInputToIso(form.closesAt)) : "not set"
    }`,
    sponsor: form.sponsor.legalName || "Sponsor not set",
    prize: form.prize.description || "Prize not set",
    eligibility: `${lines(form.eligibility.cities).length} cities on the list${
      form.eligibility.minAge ? `, minimum age ${form.eligibility.minAge}` : ""
    }`,
    rules: `Rules version ${form.rulesVersion || "not set"} · ${form.rulesApproved ? "approved" : "not approved"}`,
    emails: form.emailsEnabled ? "Giveaway emails are ON" : "Giveaway emails are off",
    app: form.appEntry.enabled
      ? `App entry ON${form.announcement.enabled ? ` · announcement ${form.announcement.at ? fmtEt(etInputToIso(form.announcement.at)) : "time not set"}` : ""}`
      : "App entry is off",
  };

  const dis = locked;
  const fieldProps = (name) => ({ name, helpOpen, toggleHelp });

  return (
    <div>
      {locked ? (
        <div className="alert alert-secondary py-2 d-flex align-items-center gap-2">
          <Icon icon="mdi:lock-outline" />
          Settings cannot change once a winner has been drawn. Everything below is view only.
        </div>
      ) : hasEntries ? (
        <div
          className="p-16 radius-8 mb-16 text-sm"
          style={{ border: "1.5px solid #F59E0B", background: "rgba(245, 158, 11, 0.08)" }}
        >
          <strong>{g.counts.total} people have already entered.</strong> If you change anything that
          affects the rules (dates, prize, sponsor, who can enter), you must give a reason and a new
          rules version. Both are kept in the audit trail.
        </div>
      ) : null}

      {SECTIONS.map((s) => (
        <SettingsSection
          key={s.id}
          id={s.id}
          icon={s.icon}
          title={s.title}
          summary={summary[s.id]}
          open={open.includes(s.id)}
          onToggle={() => toggle(s.id)}
          editable={!locked}
          dirtyCount={dirtyIn(s.id)}
        >
          {s.id === "basics" && (
            <div className="row">
              <div className="col-md-6">
                <Field label="Name" {...fieldProps("name")}>
                  <input className="form-control" disabled={dis} value={form.name} onChange={(e) => set("name", e.target.value)} />
                </Field>
              </div>
              {form.eligibility.quebecIncluded ? (
                <div className="col-md-6">
                  <Field label="Name, in French" hint="For the French rules. For example: Concours Curiouzz de 250 $ avec Mane West Salon">
                    <input className="form-control" disabled={dis} value={form.nameFr} onChange={(e) => set("nameFr", e.target.value)} />
                  </Field>
                </div>
              ) : null}
              <div className="col-md-6">
                <Field label="City" {...fieldProps("city")}>
                  <input className="form-control" disabled={dis} value={form.city} onChange={(e) => set("city", e.target.value)} />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Opens (Toronto time, ET)" {...fieldProps("opensAt")}>
                  <input
                    type="datetime-local"
                    className="form-control"
                    disabled={dis}
                    value={form.opensAt}
                    onChange={(e) => set("opensAt", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field
                  label="Closes (Toronto time, ET)"
                  {...fieldProps("closesAt")}
                  hint={datesBad ? "The closing time must be after the opening time." : null}
                >
                  <input
                    type="datetime-local"
                    className="form-control"
                    disabled={dis}
                    value={form.closesAt}
                    onChange={(e) => set("closesAt", e.target.value)}
                  />
                </Field>
              </div>
            </div>
          )}

          {s.id === "sponsor" && (
            <div className="row">
              <div className="col-md-6">
                <Field
                  label="Legal name"
                  {...fieldProps("legalName")}
                  help="The company running the giveaway, exactly as registered. It appears in the official rules."
                >
                  <input
                    className="form-control"
                    disabled={dis}
                    value={form.sponsor.legalName}
                    onChange={(e) => set("sponsor.legalName", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Contact email" {...fieldProps("contactEmail")}>
                  <input
                    type="email"
                    className="form-control"
                    disabled={dis}
                    value={form.sponsor.contactEmail}
                    onChange={(e) => set("sponsor.contactEmail", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-12">
                <Field
                  label="Mailing address"
                  {...fieldProps("mailingAddress")}
                  hint="This is printed in the public rules. Use a business address or PO box, never a home address."
                >
                  <textarea
                    className="form-control"
                    rows={3}
                    disabled={dis}
                    value={form.sponsor.mailingAddress}
                    onChange={(e) => set("sponsor.mailingAddress", e.target.value)}
                  />
                </Field>
              </div>
            </div>
          )}

          {s.id === "prize" && (
            <div className="row">
              <div className="col-12">
                <Field label="What they win" {...fieldProps("prizeDescription")}>
                  <textarea
                    className="form-control"
                    rows={2}
                    disabled={dis}
                    value={form.prize.description}
                    onChange={(e) => set("prize.description", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-md-4">
                <Field label="Value" {...fieldProps("prizeValue")} hint="Approximate retail value, as a number.">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    disabled={dis}
                    value={form.prize.value}
                    onChange={(e) => set("prize.value", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-md-4">
                <Field label="Currency" {...fieldProps("prizeCurrency")}>
                  <input
                    className="form-control"
                    disabled={dis}
                    value={form.prize.currency}
                    onChange={(e) => set("prize.currency", e.target.value.toUpperCase())}
                  />
                </Field>
              </div>
              <div className="col-md-4">
                <Field label="Provided by" {...fieldProps("prizeProvider")}>
                  <input
                    className="form-control"
                    disabled={dis}
                    value={form.prize.provider}
                    onChange={(e) => set("prize.provider", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-12">
                <Field label="Restrictions" {...fieldProps("prizeRestrictions")} hint="For example: expiry date, no cash value, not transferable.">
                  <textarea
                    className="form-control"
                    rows={2}
                    disabled={dis}
                    value={form.prize.restrictions}
                    onChange={(e) => set("prize.restrictions", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-12">
                <Check
                  label="The prize provider is a co-sponsor (the rules name them as Prize Sponsor and exclude their staff; leave off for a gift card bought from a shop)"
                  checked={form.prize.providerIsSponsor}
                  disabled={dis}
                  onChange={(v) => set("prize.providerIsSponsor", v)}
                />
              </div>
              {form.eligibility.quebecIncluded ? (
                <>
                  <div className="col-12">
                    <Field label="Prize, in French" hint="For the French rules. For example: Un (1) certificat cadeau Mane West Salon d'une valeur de 250 $ CA">
                      <input
                        className="form-control"
                        disabled={dis}
                        value={form.prize.descriptionFr}
                        onChange={(e) => set("prize.descriptionFr", e.target.value)}
                      />
                    </Field>
                  </div>
                  <div className="col-12">
                    <Field label="Restrictions, in French">
                      <textarea
                        className="form-control"
                        rows={2}
                        disabled={dis}
                        value={form.prize.restrictionsFr}
                        onChange={(e) => set("prize.restrictionsFr", e.target.value)}
                      />
                    </Field>
                  </div>
                </>
              ) : null}
            </div>
          )}

          {s.id === "eligibility" && (
            <div className="row">
              {(g.areaSources || []).length > 0 && !dis ? (
                <div className="col-12">
                  <CopyArea
                    sources={g.areaSources}
                    onCopy={(src) => {
                      const e = src.eligibility || {};
                      setForm((f) => ({
                        ...f,
                        pocketSlug: src.pocketSlug || f.pocketSlug,
                        eligibility: {
                          ...f.eligibility,
                          areaText: e.areaText || "",
                          areaTextFr: e.areaTextFr || "",
                          cities: (e.cities || []).join("\n"),
                          excludedCities: (e.excludedCities || []).join("\n"),
                          excludedMessage: e.excludedMessage || "",
                          quebecIncluded: e.quebecIncluded === true,
                        },
                      }));
                      toastOk(`Area copied from ${src.name}. Press Save to keep it.`);
                    }}
                  />
                </div>
              ) : null}
              <div className="col-md-8">
                <Field
                  label="Pocket (for the age range)"
                  hint="The announcement and reminder only go to members inside this pocket's introduction age range. Who can enter is set by the minimum age."
                >
                  <select
                    className="form-select"
                    disabled={dis}
                    value={form.pocketSlug}
                    onChange={(e) => set("pocketSlug", e.target.value)}
                  >
                    <option value="">No pocket (the announcement ignores the age range)</option>
                    {(g.pocketOptions || []).map((p) => (
                      <option key={p.slug} value={p.slug}>
                        {p.name} ({p.slug}){p.ageMin != null && p.ageMax != null ? ` · ${p.ageMin} to ${p.ageMax}` : ""}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="col-md-4">
                <Field label="Minimum age" {...fieldProps("minAge")}>
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    disabled={dis}
                    value={form.eligibility.minAge}
                    onChange={(e) => set("eligibility.minAge", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-12">
                <Field label="Area, in words" {...fieldProps("areaText")} hint="How the rules describe where people must live.">
                  <input
                    className="form-control"
                    disabled={dis}
                    value={form.eligibility.areaText}
                    onChange={(e) => set("eligibility.areaText", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field
                  label={`Cities on the list (${lines(form.eligibility.cities).length})`}
                  {...fieldProps("cities")}
                  hint="Search to find a city, or type a new one and add it. Entries from a city not on the list are flagged for review."
                >
                  <CityList
                    value={form.eligibility.cities}
                    disabled={dis}
                    onChange={(v) => set("eligibility.cities", v)}
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Cities not allowed" {...fieldProps("excludedCities")} hint="One city per line.">
                  <textarea
                    className="form-control"
                    rows={6}
                    disabled={dis}
                    value={form.eligibility.excludedCities}
                    onChange={(e) => set("eligibility.excludedCities", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-12">
                <Field label="Message for people in a city that is not allowed" {...fieldProps("excludedMessage")}>
                  <textarea
                    className="form-control"
                    rows={2}
                    disabled={dis}
                    value={form.eligibility.excludedMessage}
                    onChange={(e) => set("eligibility.excludedMessage", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-12">
                <Check
                  label="Quebec residents can enter"
                  checked={form.eligibility.quebecIncluded}
                  disabled={dis}
                  onChange={(v) => set("eligibility.quebecIncluded", v)}
                />
                <div className="text-xs text-secondary-light">
                  Quebec no longer requires filing with the RACJ (since October 27, 2023), but the rules
                  must also be in French. The French version is built automatically at
                  /giveaway/rules?lang=fr; tick "French rules approved" under Rules once it is reviewed.
                </div>
              </div>
              {form.eligibility.quebecIncluded ? (
                <div className="col-12 mt-12">
                  <Field label="Area, in French" hint="For the French rules. For example: Ottawa et Gatineau, et les municipalités environnantes de l'Ontario et du Québec énumérées dans le présent règlement">
                    <input
                      className="form-control"
                      disabled={dis}
                      value={form.eligibility.areaTextFr}
                      onChange={(e) => set("eligibility.areaTextFr", e.target.value)}
                    />
                  </Field>
                </div>
              ) : null}
            </div>
          )}

          {s.id === "rules" && (
            <div className="row">
              <div className="col-md-4">
                <Field
                  label="Rules version"
                  {...fieldProps("rulesVersion")}
                  hint={hasEntries ? "People have entered. A rule change needs a new version here, for example 1.1." : null}
                >
                  <input
                    className="form-control"
                    disabled={dis}
                    value={form.rulesVersion}
                    onChange={(e) => set("rulesVersion", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-md-4">
                <Field label="Draw date, as written in the rules" {...fieldProps("drawDateText")}>
                  <input
                    className="form-control"
                    disabled={dis}
                    value={form.drawDateText}
                    placeholder="November 30, 2026 at 12:00 PM ET"
                    onChange={(e) => set("drawDateText", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-md-2">
                <Field label="Days to respond" {...fieldProps("winnerResponseDays")} hint="For the selected person.">
                  <input
                    type="number"
                    min="1"
                    className="form-control"
                    disabled={dis}
                    value={form.winnerResponseDays}
                    onChange={(e) => set("winnerResponseDays", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-md-2">
                <Field
                  label="Keep data (days)"
                  {...fieldProps("retentionDays")}
                  help="How long entrant details are kept after the giveaway ends. After that they should be deleted with the button at the bottom of this page."
                >
                  <input
                    type="number"
                    min="1"
                    className="form-control"
                    disabled={dis}
                    value={form.retentionDays}
                    onChange={(e) => set("retentionDays", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-12">
                <Check
                  label="Rules approved (the official rules have been reviewed and signed off)"
                  checked={form.rulesApproved}
                  disabled={dis}
                  onChange={(v) => set("rulesApproved", v)}
                />
                {form.eligibility.quebecIncluded ? (
                  <Check
                    label="French rules approved (the French version has been reviewed and signed off)"
                    checked={form.frenchRulesApproved}
                    disabled={dis}
                    onChange={(v) => set("frenchRulesApproved", v)}
                  />
                ) : null}
                <Check
                  label="Prize purchased (we already have the prize in hand)"
                  checked={form.prizePurchased}
                  disabled={dis}
                  onChange={(v) => set("prizePurchased", v)}
                />
              </div>
            </div>
          )}

          {s.id === "emails" && (
            <div>
              <Check
                switchStyle
                label="Send giveaway emails"
                checked={form.emailsEnabled}
                disabled={dis}
                onChange={(v) => set("emailsEnabled", v)}
              />
              <div
                className="p-12 radius-8 text-sm"
                style={{ border: "1px solid rgba(250, 105, 185, 0.5)", background: "rgba(250, 105, 185, 0.06)" }}
              >
                When this is on, real entrants get real emails: the confirmation after they enter, and
                the winner emails you send from the Draw and winner tab. Leave it off while testing.
              </div>
            </div>
          )}
          {s.id === "app" && (
            <div>
              <Check
                switchStyle
                label="Enter members automatically through the app"
                checked={form.appEntry.enabled}
                disabled={dis}
                onChange={(v) => set("appEntry.enabled", v)}
              />
              <div className="ms-4 mb-12">
                <Check
                  label="New members: entered when they finish registering, Gatekeeper included, while the giveaway is open"
                  checked={form.appEntry.newMembers}
                  disabled={dis || !form.appEntry.enabled}
                  onChange={(v) => set("appEntry.newMembers", v)}
                />
                <Check
                  label="Existing members: entered the first time they open Curiouzz while the giveaway is open"
                  checked={form.appEntry.existingMembers}
                  disabled={dis || !form.appEntry.enabled}
                  onChange={(v) => set("appEntry.existingMembers", v)}
                />
                <Check
                  label={'Push to new members when they are entered ("You\'re in the giveaway")'}
                  checked={form.appEntry.pushOnEntry}
                  disabled={dis || !form.appEntry.enabled}
                  onChange={(v) => set("appEntry.pushOnEntry", v)}
                />
              </div>
              <div className="text-xs text-secondary-light mb-16">
                Only members who live in an eligible community and meet the minimum age are entered.
                Everyone gets one entry, however they enter. A download alone enters nobody. The web
                form stays open as the free way to enter without the app.
              </div>

              <Check
                switchStyle
                label="Send the announcement to existing members"
                checked={form.announcement.enabled}
                disabled={dis || !!original.announcement?.sentAt}
                onChange={(v) => set("announcement.enabled", v)}
              />
              <div className="row gy-2 ms-4">
                <div className="col-md-6">
                  <Field label="Send at (Toronto time, ET)" {...fieldProps("announcementAt")}>
                    <input
                      type="datetime-local"
                      className="form-control"
                      disabled={dis || !!original.announcement?.sentAt}
                      value={form.announcement.at}
                      onChange={(e) => set("announcement.at", e.target.value)}
                    />
                  </Field>
                </div>
                <div className="col-md-6 d-flex align-items-end">
                  <Check
                    label="Also send a push"
                    checked={form.announcement.push}
                    disabled={dis || !!original.announcement?.sentAt}
                    onChange={(v) => set("announcement.push", v)}
                  />
                </div>
              </div>
              <div className="row gy-2 ms-4">
                <div className="col-md-6">
                  <Field
                    label="Reminder push (Toronto time, ET)"
                    {...fieldProps("reminderAt")}
                    hint={'Optional. One push, "Last week: win ...", to the same members who are still not entered.'}
                  >
                    <input
                      type="datetime-local"
                      className="form-control"
                      disabled={dis || !!original.announcement?.reminderSentAt}
                      value={form.announcement.reminderAt}
                      onChange={(e) => set("announcement.reminderAt", e.target.value)}
                    />
                  </Field>
                </div>
              </div>
              <div
                className="p-12 radius-8 text-sm mt-8"
                style={{ border: "1px solid rgba(250, 105, 185, 0.5)", background: "rgba(250, 105, 185, 0.06)" }}
              >
                Sent once, at that time, to members in the eligible area who are inside the city's
                introduction age range and are not entered yet. The email goes only when "Send
                giveaway emails" is on, and never to anyone who unsubscribed.
              </div>
              {original.announcement?.sentAt ? (
                <div className="text-sm mt-12">
                  Sent {fmtEt(original.announcement.sentAt)}:{" "}
                  {original.announcement.summary
                    ? `${original.announcement.summary.emailed || 0} emails, ${original.announcement.summary.pushed || 0} pushes; left out: ${
                        original.announcement.summary.dropped?.outside_band || 0
                      } outside the age range, ${original.announcement.summary.dropped?.already_entered || 0} already entered, ${
                        original.announcement.summary.dropped?.email_opted_out || 0
                      } unsubscribed from email`
                    : "details pending"}
                </div>
              ) : null}
            </div>
          )}
        </SettingsSection>
      ))}

      {/* Save bar */}
      {!locked ? (
        <div className="card mt-24 radius-12">
          <div className="card-body p-24">
            {hasEntries ? (
              <div className="mb-16">
                <label className="form-label text-sm fw-semibold">Reason for this change</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={reason}
                  placeholder="For example: the sponsor extended the closing date by one week"
                  onChange={(e) => setReason(e.target.value)}
                />
                <div className="text-xs mt-1" style={{ color: reasonShort ? "#FA69B9" : undefined }}>
                  Needed when a rule-affecting setting changes after people have entered. At least 10
                  characters.
                </div>
              </div>
            ) : null}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <span className="text-sm text-secondary-light">
                {changedKeys.length
                  ? `${changedKeys.length} unsaved change${changedKeys.length === 1 ? "" : "s"}`
                  : "No unsaved changes"}
              </span>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={!changedKeys.length || saving}
                  onClick={() => {
                    setForm(original);
                    setReason("");
                  }}
                >
                  Undo changes
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={!changedKeys.length || saving || datesBad || reasonShort}
                  onClick={save}
                >
                  {saving ? "Saving…" : "Save settings"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Danger zone */}
      <div className="card mt-24 radius-12" style={{ border: "1.5px solid rgba(220, 53, 69, 0.5)" }}>
        <div className="card-body p-24">
          <h6 className="fw-semibold mb-1 d-flex align-items-center gap-2">
            <Icon icon="mdi:alert-octagon-outline" style={{ color: "#dc3545" }} /> Danger zone
          </h6>
          <p className="text-sm text-secondary-light mb-12" style={{ maxWidth: 760 }}>
            Permanently delete the personal details of everyone who entered (names, emails, cities).
            Counts and the audit trail are kept. This cannot be undone. Normally done once the
            retention period ({g.retentionDays || "?"} days after the end) is over.
          </p>
          <Check
            label="Delete now, even though the retention period may not be over yet"
            checked={purgeEarly}
            onChange={setPurgeEarly}
          />
          <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setPurgeOpen(true)}>
            Delete entrant data
          </button>
        </div>
      </div>

      <ActionModal
        show={purgeOpen}
        title="Delete entrant data for good?"
        body={
          <>
            <p className="mb-8">
              Every entrant's personal details for <strong>{g.name}</strong> will be erased. This cannot be
              undone.
            </p>
            {purgeEarly ? <p className="mb-0">You chose to delete before the retention period ends.</p> : null}
          </>
        }
        confirmLabel="Delete for good"
        danger
        typeToConfirm={g.slug}
        onClose={() => setPurgeOpen(false)}
        onConfirm={async () => {
          try {
            await purgeGiveaway(g._id, purgeEarly ? { confirm: g.slug, early: true } : { confirm: g.slug });
            toastOk("Entrant data deleted");
            await reload();
            return true;
          } catch (e) {
            if (e?.response?.status === 401) {
              onUnauthorized();
              return true;
            }
            showProblem(apiProblem(e, "Could not delete the entrant data."));
            return false;
          }
        }}
      />
    </div>
  );
};

export default GiveawaySettingsTab;
